import React from 'react';
import { Target, Scale, Users, Building } from 'lucide-react';
import { objectivesData } from '../mockData';

const iconComponents = {
  Target,
  Scale,
  Users
};

const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            About the DDA
          </h1>
          <p className="text-lg text-gray-600">
            Learn about our purpose, structure, and commitment to Hiri Koiari District
          </p>
        </div>
      </section>

      {/* Purpose Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-gray-900">Purpose of the DDA</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  The Hiri Koiari District Development Authority (DDA) is a statutory body established
                  under the <strong>District Development Authority Act 2014</strong> of Papua New Guinea. Our
                  primary purpose is to manage and accelerate development at the district level
                  through effective coordination of infrastructure and social services.
                </p>
                <p>
                  As a legal entity, we directly manage and coordinate improvements to local service
                  delivery, working closely with local-level governments, community leaders, and
                  stakeholders to ensure that development initiatives meet the real needs of our
                  communities.
                </p>
                <p>
                  The DDA serves as the bridge between national policy and local implementation,
                  ensuring that resources are allocated effectively and that development projects align
                  with both community priorities and national development goals.
                </p>
              </div>
            </div>

            {/* Key Objectives */}
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-gray-900">Key Objectives</h2>
              <div className="space-y-4">
                {objectivesData.map((objective, index) => {
                  const IconComponent = iconComponents[objective.icon];
                  return (
                    <div
                      key={objective.id}
                      className="flex items-start space-x-4 p-6 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <div className="w-12 h-12 bg-[#F5B700] rounded-full flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-6 h-6 text-gray-900" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                          {objective.title}
                        </h3>
                        <p className="text-gray-700">{objective.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DDA Structure Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">DDA Structure</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* DDA Board */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-24 h-24 bg-[#F5B700] rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-12 h-12 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-6">DDA Board</h3>
              <p className="text-gray-600 text-center mb-6 leading-relaxed">
                Chaired by the local Member of Parliament, the Board includes presidents of Local-Level
                Governments and provides strategic oversight.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start space-x-2">
                  <span className="w-2 h-2 bg-[#F5B700] rounded-full mt-2 flex-shrink-0"></span>
                  <span className="text-gray-700">Sets strategic direction</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="w-2 h-2 bg-[#F5B700] rounded-full mt-2 flex-shrink-0"></span>
                  <span className="text-gray-700">Approves development plans</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="w-2 h-2 bg-[#F5B700] rounded-full mt-2 flex-shrink-0"></span>
                  <span className="text-gray-700">Oversees budget allocation</span>
                </li>
              </ul>
            </div>

            {/* District Administrator */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-24 h-24 bg-[#F5B700] rounded-full flex items-center justify-center mx-auto mb-6">
                <Building className="w-12 h-12 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-6">District Administrator</h3>
              <p className="text-gray-600 text-center mb-6 leading-relaxed">
                Responsible for day-to-day operations and implementation of board decisions.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start space-x-2">
                  <span className="w-2 h-2 bg-[#F5B700] rounded-full mt-2 flex-shrink-0"></span>
                  <span className="text-gray-700">Manages operations</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="w-2 h-2 bg-[#F5B700] rounded-full mt-2 flex-shrink-0"></span>
                  <span className="text-gray-700">Implements policies</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="w-2 h-2 bg-[#F5B700] rounded-full mt-2 flex-shrink-0"></span>
                  <span className="text-gray-700">Coordinates programs</span>
                </li>
              </ul>
            </div>

            {/* District Staff */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-24 h-24 bg-[#F5B700] rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-12 h-12 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-6">District Staff</h3>
              <p className="text-gray-600 text-center mb-6 leading-relaxed">
                Dedicated teams across sectors including health, education, infrastructure, and community
                development.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start space-x-2">
                  <span className="w-2 h-2 bg-[#F5B700] rounded-full mt-2 flex-shrink-0"></span>
                  <span className="text-gray-700">Project implementation</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="w-2 h-2 bg-[#F5B700] rounded-full mt-2 flex-shrink-0"></span>
                  <span className="text-gray-700">Service delivery</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="w-2 h-2 bg-[#F5B700] rounded-full mt-2 flex-shrink-0"></span>
                  <span className="text-gray-700">Community engagement</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Our Leadership Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Our Leadership</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {/* Board Chairman */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1">
              <div className="w-40 h-40 mx-auto mb-6 rounded-full overflow-hidden border-4 border-[#3B5FA9] shadow-lg">
                <img
                  src="/keith_iduhu.jpg"
                  alt="Hon. Keith Iduhu"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-2">
                Hon. Keith Iduhu, LLB, MP
              </h3>
              <p className="text-[#3B5FA9] font-semibold text-center">DDA Board Chairman</p>
            </div>

            {/* District Administrator */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1">
              <div className="w-40 h-40 mx-auto mb-6 rounded-full overflow-hidden border-4 border-[#3B5FA9] shadow-lg">
                <img
                  src="/sibona_mavaru.jpg"
                  alt="Sibona Mavaru"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-2">Sibona Mavaru</h3>
              <p className="text-[#3B5FA9] font-semibold text-center">District Administrator</p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Functions & Authority */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Core Functions */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Core Functions</h2>
              <div className="space-y-6">
                <div className="border-l-4 border-red-500 pl-6 py-4 bg-white rounded-r-xl shadow-md hover:shadow-lg transition-shadow">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Development Planning</h3>
                  <p className="text-gray-600">
                    Creating comprehensive five-year district development plans aligned with national
                    priorities
                  </p>
                </div>
                <div className="border-l-4 border-red-500 pl-6 py-4 bg-white rounded-r-xl shadow-md hover:shadow-lg transition-shadow">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Budget Management</h3>
                  <p className="text-gray-600">
                    Approving and managing Local-Level Government budgets and district-wide allocations
                  </p>
                </div>
                <div className="border-l-4 border-red-500 pl-6 py-4 bg-white rounded-r-xl shadow-md hover:shadow-lg transition-shadow">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Service Coordination</h3>
                  <p className="text-gray-600">
                    Coordinating delivery of health, education, and infrastructure services
                  </p>
                </div>
                <div className="border-l-4 border-red-500 pl-6 py-4 bg-white rounded-r-xl shadow-md hover:shadow-lg transition-shadow">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Procurement Oversight</h3>
                  <p className="text-gray-600">
                    Managing procurement processes for district development projects
                  </p>
                </div>
              </div>
            </div>

            {/* Authority & Powers */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Authority & Powers</h2>
              <div className="space-y-6">
                <div className="border-l-4 border-[#F5B700] pl-6 py-4 bg-white rounded-r-xl shadow-md hover:shadow-lg transition-shadow">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Property Management</h3>
                  <p className="text-gray-600">
                    Authority to acquire, hold, and dispose of property for district development
                  </p>
                </div>
                <div className="border-l-4 border-[#F5B700] pl-6 py-4 bg-white rounded-r-xl shadow-md hover:shadow-lg transition-shadow">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Contract Authority</h3>
                  <p className="text-gray-600">
                    Power to enter into contracts for services and infrastructure projects
                  </p>
                </div>
                <div className="border-l-4 border-[#F5B700] pl-6 py-4 bg-white rounded-r-xl shadow-md hover:shadow-lg transition-shadow">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Personnel Management</h3>
                  <p className="text-gray-600">
                    Managing district staff including health and education personnel
                  </p>
                </div>
                <div className="border-l-4 border-[#F5B700] pl-6 py-4 bg-white rounded-r-xl shadow-md hover:shadow-lg transition-shadow">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Financial Control</h3>
                  <p className="text-gray-600">
                    Operating under Public Finances (Management) Act 1995 with strict accountability
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl p-8 text-white hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
              <h3 className="text-2xl font-bold mb-4">Our Vision</h3>
              <p className="text-lg leading-relaxed">
                A prosperous and sustainable Hiri Koiari District with quality infrastructure,
                accessible services, and empowered communities.
              </p>
            </div>
            <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-2xl p-8 text-gray-900 hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
              <h3 className="text-2xl font-bold mb-4">Our Mission</h3>
              <p className="text-lg leading-relaxed">
                To coordinate and deliver responsive, transparent, and accountable development
                programs that improve the wellbeing of all Hiri Koiari residents.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;